import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, HelpCircle, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "@/Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Collapse, Input, Label, Row } from "@/Components/ui";
import { groupReservationService } from "@/api/services/group-reservation.service";
import { GroupReservationRequest } from "@/api/types/group-reservation";
import GroupReservationPageShell from "../GroupReservationPageShell";
import {
  buildGroupReservationCalendarWeeks,
  formatGroupReservationDateRange,
  formatGroupReservationCalendarMonthTitle,
  getGroupReservationCalendarDaySummary,
  getGroupReservationSourceLabel,
  getGroupReservationStatusLabel,
  getGroupReservationTypeLabel,
  GROUP_RESERVATION_STATUS_OPTIONS,
} from "./groupReservationAdmin.mjs";
import GroupReservationGuideModal from "./components/GroupReservationGuideModal";

const SOURCE_OPTIONS = [
  { value: "", label: "전체" },
  { value: "public", label: "일반 링크" },
  { value: "agency", label: "대리점 링크" },
];
const CALENDAR_WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const CALENDAR_PAGE_SIZE = 200;

const statusColor = (status: string) => {
  if (status === "approved") return "success";
  if (status === "rejected" || status === "reverted" || status === "canceled") return "danger";
  if (status === "awaiting_bank_transfer" || status === "bank_transfer_confirmed") return "warning";
  if (status === "reservation_created") return "info";
  return "secondary";
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value?: number | null) => {
  if (!value) return "-";
  return `${Number(value).toLocaleString()}원`;
};

const matchesSearch = (request: GroupReservationRequest, search: string) => {
  const keyword = search.trim().toLowerCase();
  if (!keyword) return true;
  return [
    request.request_code,
    request.group_name,
    request.applicant_name,
    request.applicant_phone,
    request.agency_label || "",
  ].some((value) => String(value || "").toLowerCase().includes(keyword));
};

export default function GroupReservationRequestsPage() {
  const navigate = useNavigate();
  const [isCalendarOpen, setIsCalendarOpen] = useState(true);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [filters, setFilters] = useState({
    status: "",
    source_type: "",
    search: "",
    visit_date: "",
    page: 1,
  });

  const requestQuery = useQuery({
    queryKey: [
      "group-reservation",
      "admin-requests",
      filters.status,
      filters.source_type,
      filters.visit_date,
      filters.page,
    ],
    queryFn: () =>
      groupReservationService.getRequests({
        status: filters.status,
        source_type: filters.source_type,
        visit_date: filters.visit_date,
        page: filters.page,
      }),
  });

  const response = requestQuery.data?.data;
  const requests = response?.results ?? [];
  const visibleRequests = useMemo(
    () => requests.filter((request) => matchesSearch(request, filters.search)),
    [requests, filters.search]
  );

  const calendarQuery = useQuery({
    queryKey: ["group-reservation", "admin-requests-calendar", filters.status, filters.source_type],
    queryFn: () =>
      groupReservationService.getRequests({
        status: filters.status,
        source_type: filters.source_type,
        page_size: CALENDAR_PAGE_SIZE,
      }),
    enabled: isCalendarOpen,
  });
  const calendarResponse = calendarQuery.data?.data;
  const calendarRequests = useMemo(
    () => (calendarResponse?.results ?? []).filter((request) => matchesSearch(request, filters.search)),
    [calendarResponse?.results, filters.search]
  );
  const calendarWeeks = useMemo(
    () => buildGroupReservationCalendarWeeks(calendarMonth, calendarRequests),
    [calendarMonth, calendarRequests]
  );
  const calendarSummary = useMemo(
    () => getGroupReservationCalendarDaySummary(calendarRequests),
    [calendarRequests]
  );

  const updateFilter = (key: keyof typeof filters, value: string | number) => {
    setFilters((previous) => ({ ...previous, [key]: value, page: key === "page" ? Number(value) : 1 }));
  };

  const selectCalendarDate = (dateKey: string) => {
    updateFilter("visit_date", dateKey);
  };

  const moveCalendarMonth = (offset: number) => {
    setCalendarMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() + offset, 1));
  };

  return (
    <GroupReservationPageShell>
      <BreadCrumb title="단체예약 신청 관리" pageTitle="예약" />
      <div className="mb-3 d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-sm-end gap-2">
        <Button color="dark" className="w-full sm:w-auto" onClick={() => navigate("/group-reservation-requests/new")}>
          <Plus size={16} className="me-1" />
          신청 등록
        </Button>
        <Button color="soft-primary" className="w-full sm:w-auto" onClick={() => setIsGuideOpen(true)}>
          <HelpCircle size={16} className="me-1" />
          사용 안내
        </Button>
      </div>
      <GroupReservationGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      <Card className="mb-3">
        <CardHeader>신청 필터</CardHeader>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Label htmlFor="group-reservation-status">상태</Label>
              <Input
                id="group-reservation-status"
                type="select"
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
              >
                {GROUP_RESERVATION_STATUS_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label htmlFor="group-reservation-source">신청 경로</Label>
              <Input
                id="group-reservation-source"
                type="select"
                value={filters.source_type}
                onChange={(event) => updateFilter("source_type", event.target.value)}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label htmlFor="group-reservation-search">검색</Label>
              <Input
                id="group-reservation-search"
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                placeholder="단체명, 신청자, 전화번호, 접수번호"
              />
            </Col>
            <Col md={2}>
              <Button color="soft-secondary" className="w-100" onClick={() => requestQuery.refetch()}>
                새로고침
              </Button>
            </Col>
          </Row>
          {filters.visit_date ? (
            <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
              <Badge color="info">선택 날짜 {filters.visit_date}</Badge>
              <Button color="soft-secondary" size="sm" onClick={() => updateFilter("visit_date", "")}>
                날짜 필터 해제
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2">
            <CalendarDays size={18} />
            관리 캘린더
          </div>
          <Button
            color="soft-secondary"
            size="sm"
            aria-expanded={isCalendarOpen}
            onClick={() => setIsCalendarOpen((previous) => !previous)}
          >
            {isCalendarOpen ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
            {isCalendarOpen ? "접기" : "펼치기"}
          </Button>
        </CardHeader>
        <Collapse isOpen={isCalendarOpen}>
          <CardBody>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <Button color="soft-secondary" size="sm" aria-label="이전 달" onClick={() => moveCalendarMonth(-1)}>
                  <ChevronLeft size={16} />
                </Button>
                <span className="fw-semibold">{formatGroupReservationCalendarMonthTitle(calendarMonth)}</span>
                <Button color="soft-secondary" size="sm" aria-label="다음 달" onClick={() => moveCalendarMonth(1)}>
                  <ChevronRight size={16} />
                </Button>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 text-muted fs-12">
                <span>신청 {calendarSummary.requestCount.toLocaleString()}건</span>
                <span>인원 {calendarSummary.headcount.toLocaleString()}명</span>
                <span>대리점 {calendarSummary.agencyCount.toLocaleString()}건</span>
                <span>대기 {calendarSummary.pendingCount.toLocaleString()}건</span>
                <span>승인 {calendarSummary.approvedCount.toLocaleString()}건</span>
              </div>
            </div>

            {calendarQuery.isLoading ? (
              <div className="py-5 text-center text-muted">관리 캘린더를 불러오는 중입니다.</div>
            ) : calendarQuery.isError ? (
              <div className="py-5 text-center text-danger">관리 캘린더를 불러오지 못했습니다.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      {CALENDAR_WEEKDAY_LABELS.map((label) => (
                        <th key={label} className="text-center fs-12">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {calendarWeeks.map((week, weekIndex) => (
                      <tr key={`week-${weekIndex}`}>
                        {week.map((day) => {
                          const daySummary = getGroupReservationCalendarDaySummary(day.requests);
                          const isSelectedDate = filters.visit_date === day.dateKey;
                          return (
                            <td
                              key={day.dateKey}
                              className={
                                isSelectedDate
                                  ? "bg-primary-subtle"
                                  : day.isCurrentMonth
                                    ? "bg-white"
                                    : "bg-light text-muted"
                              }
                              style={{ minWidth: 150, height: 124, verticalAlign: "top", cursor: "pointer" }}
                              onClick={() => selectCalendarDate(day.dateKey)}
                            >
                              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                <button
                                  type="button"
                                  className={`border-0 bg-transparent p-0 fw-semibold fs-13 ${
                                    isSelectedDate ? "text-primary" : day.isCurrentMonth ? "text-body" : "text-muted"
                                  }`}
                                  aria-label={`${day.dateKey} 신청 목록 보기`}
                                  aria-pressed={isSelectedDate}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    selectCalendarDate(day.dateKey);
                                  }}
                                >
                                  {day.dayOfMonth}
                                </button>
                                {daySummary.requestCount > 0 ? (
                                  <Badge color={daySummary.pendingCount > 0 ? "warning" : "success"}>
                                    {daySummary.requestCount}건
                                  </Badge>
                                ) : null}
                              </div>
                              {daySummary.requestCount > 0 ? (
                                <>
                                  <div className="text-muted fs-12 mb-1">{daySummary.headcount.toLocaleString()}명</div>
                                  <div className="d-grid gap-1">
                                    {day.requests.slice(0, 2).map((request) => (
                                      <button
                                        key={`${day.dateKey}-${request.id}`}
                                        type="button"
                                        className="border-0 bg-transparent p-0 text-start text-primary fs-12 text-truncate"
                                        title={request.group_name}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          navigate(`/group-reservation-requests/${request.id}`);
                                        }}
                                      >
                                        {request.group_name}
                                      </button>
                                    ))}
                                    {day.requests.length > 2 ? (
                                      <span className="text-muted fs-12">외 {day.requests.length - 2}건</span>
                                    ) : null}
                                  </div>
                                </>
                              ) : (
                                <div className="text-muted fs-12">-</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Collapse>
      </Card>

      <Card>
        <CardHeader>
          신청 목록
          <span className="ms-2 text-muted fs-12">총 {response?.count ?? visibleRequests.length}건</span>
        </CardHeader>
        <CardBody>
          {requestQuery.isLoading ? (
            <div className="py-5 text-center text-muted">신청 목록을 불러오는 중입니다.</div>
          ) : requestQuery.isError ? (
            <div className="py-5 text-center text-danger">신청 목록을 불러오지 못했습니다.</div>
          ) : visibleRequests.length === 0 ? (
            <div className="py-5 text-center text-muted">조건에 맞는 단체예약 신청이 없습니다.</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>접수번호</th>
                    <th>접수일</th>
                    <th>희망일</th>
                    <th>단체명</th>
                    <th>타입</th>
                    <th>인원</th>
                    <th>신청자</th>
                    <th>접수경로</th>
                    <th>상태</th>
                    <th>예상금액</th>
                    <th>대리점 수수료</th>
                    <th>입금</th>
                    <th>연결예약</th>
                    <th className="text-end">액션</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <div className="fw-semibold">{request.request_code}</div>
                        <div className="text-muted fs-12">#{request.id}</div>
                      </td>
                      <td>{formatDateTime(request.created_at)}</td>
                      <td>{formatGroupReservationDateRange(request)}</td>
                      <td>{request.group_name}</td>
                      <td>{getGroupReservationTypeLabel(request)}</td>
                      <td>{request.headcount?.toLocaleString()}명</td>
                      <td>
                        <div>{request.applicant_name}</div>
                        <div className="text-muted fs-12">{request.applicant_phone}</div>
                      </td>
                      <td>
                        <Badge color={request.source_type === "agency" ? "info" : "secondary"}>
                          {getGroupReservationSourceLabel(request)}
                        </Badge>
                      </td>
                      <td>
                        <Badge color={statusColor(request.status)}>
                          {getGroupReservationStatusLabel(request.status)}
                        </Badge>
                      </td>
                      <td>{formatAmount(request.estimated_total_amount)}</td>
                      <td>
                        <div>{formatAmount(request.agency_commission_amount)}</div>
                        <div className="text-muted fs-12">
                          {request.agency_commission_rate_snapshot ? `${request.agency_commission_rate_snapshot}%` : "-"}
                        </div>
                      </td>
                      <td>
                        <div>{formatAmount(request.bank_transfer_amount)}</div>
                        <div className="text-muted fs-12">{request.bank_transfer_depositor || "-"}</div>
                      </td>
                      <td>{request.linked_reservation_count || 0}건</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          color="soft-primary"
                          onClick={() => navigate(`/group-reservation-requests/${request.id}`)}
                        >
                          상세
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 d-flex justify-content-between align-items-center">
            <Button
              color="soft-secondary"
              disabled={!response?.previous}
              onClick={() => updateFilter("page", Math.max(1, filters.page - 1))}
            >
              이전
            </Button>
            <span className="text-muted fs-13">{filters.page} 페이지</span>
            <Button
              color="soft-secondary"
              disabled={!response?.next}
              onClick={() => updateFilter("page", filters.page + 1)}
            >
              다음
            </Button>
          </div>
        </CardBody>
      </Card>
    </GroupReservationPageShell>
  );
}
